import IconArrowRight from 'assets/icons/arrow-right.svg';
import IconChevronUp from 'assets/icons/chevron-up-stroke.svg';
import IconCheckCircle from 'assets/icons/circle-check.svg';
import cn from 'classnames';
import LazyMotionWrapper from 'components/lazy-motion-wrapper';
import { useConfig } from 'config/use-config';
import { useCart } from 'contexts/cart';
import useToFulfillCouponMOV from 'hooks/cart/use-to-fullfill-coupon-mov';
import useMobile from 'hooks/common/use-mobile';
import { AnimatePresence, m } from 'motion/react';
import { useRouter } from 'next/router';
import React, { CSSProperties } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CURRENCY_2_DECIMALS } from 'constants/formatters';
import { stripRegionPathPrefix } from 'utils/routing/strip-region-path-prefix';

interface Props {
  showToggleOnMobile?: boolean;
  additionalStyle?: CSSProperties;
}

export const CheckoutCoupon: React.FC<Props> = ({
  showToggleOnMobile = false,
  additionalStyle,
}) => {
  const { currencyCode } = useConfig();

  const { pathname } = useRouter();
  const [couponCodeText, setCouponCodeText] = React.useState('');
  const [expanded, setExpanded] = React.useState(false);
  const { formatNumber } = useIntl();

  const {
    canUseCoupon,
    setCouponCode,
    couponCode,
    effectiveCartErrors,
    effectiveCart,
    loading,
    discount,
  } = useCart();

  const { toFulfillCouponMOV } = useToFulfillCouponMOV({ effectiveCart });

  const isMobile = useMobile();
  const intl = useIntl();
  const isCartPage = React.useMemo(() => {
    if (stripRegionPathPrefix(pathname) === '/cart') {
      return true;
    }
    return false;
  }, [pathname]);

  React.useEffect(() => {
    if (couponCode === '') {
      setCouponCodeText('');
      setExpanded(false);
    }
  }, [couponCode]);

  React.useEffect(() => {
    if (couponCode && couponCode !== '' && couponCode !== null) {
      setCouponCodeText(couponCode);
      setExpanded(true);
    }
  }, [couponCode]);

  const submit = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      setCouponCode(couponCodeText);
    },
    [setCouponCode, couponCodeText]
  );

  const expandForm = React.useMemo(() => {
    return (!showToggleOnMobile && isMobile) || expanded;
  }, [expanded, showToggleOnMobile, isMobile]);

  if (!canUseCoupon) {
    return <></>;
  }

  return (
    <LazyMotionWrapper>
      <div
        data-test="coupon-section"
        className="lg:bg-other-white lg:rounded-lg lg:px-6 lg:py-4 lg:shadow-sm"
        style={additionalStyle}
      >
        <button
          className={cn(
            'focus-default flex w-full items-center justify-between rounded-sm pt-1 leading-1 lg:flex',
            !showToggleOnMobile && 'hidden'
          )}
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center">
            <div className="flex flex-col items-start justify-start">
              <div className="text-primary-main font-black">
                <FormattedMessage id="checkout:coupon:have-coupon" />
              </div>
            </div>
          </div>

          <m.div
            initial={{ rotate: 0 }}
            animate={{ rotate: expandForm ? 0 : 180 }}
            transition={{ duration: 0.5, ease: [0.04, 0.62, 0.23, 0.98] }}
          >
            <IconChevronUp className="h-6 w-6" />
          </m.div>
        </button>

        <AnimatePresence>
          {expandForm && (
            <m.div
              initial="collapsed"
              animate="open"
              exit="collapsed"
              variants={{
                open: { opacity: 1, height: 'auto' },
                collapsed: { opacity: 0, height: 0 },
              }}
              transition={{ duration: 0.8, ease: [0.04, 0.62, 0.23, 0.98] }}
              className="overflow-hidden"
            >
              {effectiveCartErrors &&
                effectiveCartErrors.discount &&
                effectiveCartErrors.discount.type === 'Coupon' && (
                  <div
                    data-test="coupon-error-box"
                    className="mx-4 mt-3 -mb-1 hidden rounded-sm border border-red-300 bg-red-100 p-2 px-4 text-sm font-medium text-red-500 lg:mx-0 lg:block"
                  >
                    {toFulfillCouponMOV ? (
                      <FormattedMessage
                        id={`checkout:coupon:error:${effectiveCartErrors.discount.error}`}
                        values={{
                          amount: formatNumber(toFulfillCouponMOV, {
                            style: 'currency',
                            currency: currencyCode,
                            ...CURRENCY_2_DECIMALS,
                          }),
                        }}
                      />
                    ) : (
                      <FormattedMessage
                        id={`checkout:coupon:error:${effectiveCartErrors.discount.error}`}
                      />
                    )}
                  </div>
                )}

              <div className={cn('mb-2 flex items-center px-4 lg:hidden', isCartPage && 'hidden')}>
                <div className="flex flex-col items-start justify-start">
                  <div className="text-primary-main font-black">
                    <FormattedMessage id="checkout:coupon:have-coupon" />
                  </div>
                </div>
              </div>

              {effectiveCartErrors &&
                effectiveCartErrors.discount &&
                effectiveCartErrors.discount.type === 'Coupon' && (
                  <div
                    data-test="coupon-error-box"
                    className={cn(
                      'mt-3 -mb-1 rounded-sm border border-red-300 bg-red-100 p-2 px-4 text-sm font-medium text-red-500 lg:mx-0 lg:hidden',
                      isCartPage ? 'mx-0' : 'mx-4'
                    )}
                  >
                    {toFulfillCouponMOV ? (
                      <FormattedMessage
                        id={`checkout:coupon:error:${effectiveCartErrors.discount.error}`}
                        values={{
                          amount: formatNumber(toFulfillCouponMOV, {
                            style: 'currency',
                            ...CURRENCY_2_DECIMALS,
                          }),
                        }}
                      />
                    ) : (
                      <FormattedMessage
                        id={`checkout:coupon:error:${effectiveCartErrors.discount.error}`}
                      />
                    )}
                  </div>
                )}

              <form
                onSubmit={submit}
                className={cn('py-2 lg:px-0 lg:py-0 lg:pt-3', isCartPage ? 'px-0' : 'px-4')}
              >
                <div
                  data-test="coupon-inputBox"
                  className={cn(
                    discount?.code ? 'border-success-main' : 'border-grey-200',
                    'bg-other-white flex items-center rounded-sm border p-1'
                  )}
                >
                  <input
                    placeholder={intl.formatMessage({
                      id: 'checkout:coupon:coupon-code-placeholder',
                    })}
                    value={couponCodeText}
                    onChange={(e) => setCouponCodeText(e.target.value)}
                    className="text-text-light-bg-secondary placeholder-grey-500 flex-1 px-2 pt-px focus:outline-hidden lg:font-normal"
                    name={'couponInput'}
                  />
                  {discount?.code && (
                    <IconCheckCircle className="text-success-main mr-3 h-4 px-2" />
                  )}
                  {/* show loading indicator while cart is loading. could be separated component  */}
                  {loading && (
                    <svg
                      className="text-primary-main mr-3 -ml-1 h-5 w-5 animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  )}

                  <button
                    type="submit"
                    className="focus-default bg-primary-main text-other-white hover:bg-primary-main/90 flex h-8 w-8 items-center justify-center rounded-xs"
                  >
                    <IconArrowRight className="text-text-dark-bg-contrast-white h-6 w-6" />
                  </button>
                </div>
              </form>
            </m.div>
          )}
        </AnimatePresence>
      </div>
    </LazyMotionWrapper>
  );
};
